import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { TenantProvider } from '@/contexts/TenantContext';
import ProtectedRoute from '@/components/ProtectedRoute';

// Public Pages
import LandingPage from '@/pages/public/LandingPage';
import FeaturesPage from '@/pages/public/FeaturesPage';
import PricingPage from '@/pages/public/PricingPage';
import AboutPage from '@/pages/public/AboutPage';
import ContactPage from '@/pages/public/ContactPage';
import CareersPage from '@/pages/public/CareersPage';

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage';
import AccountTypeSelection from '@/pages/auth/AccountTypeSelection';
import IndividualSignup from '@/pages/auth/IndividualSignup';
import OrganizationSignup from '@/pages/auth/OrganizationSignup';
import JoinTeamPage from '@/pages/auth/JoinTeamPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage';

// Dashboard Pages
import CandidateDashboard from '@/pages/dashboards/CandidateDashboard';
import RecruiterDashboard from '@/pages/dashboards/RecruiterDashboard';
import HiringManagerDashboard from '@/pages/dashboards/HiringManagerDashboard';
import HRAdminDashboard from '@/pages/dashboards/HRAdminDashboard';
import OrgOwnerDashboard from '@/pages/dashboards/OrgOwnerDashboard';
import SuperAdminDashboard from '@/pages/dashboards/SuperAdminDashboard';

// Error Pages
import AccessDenied from '@/pages/AccessDenied';

// Profile Pages
import ProfilePage from '@/pages/ProfilePage';
import ProfileEditPage from '@/pages/ProfileEditPage';

// Organization Pages
import OrganizationSettings from '@/pages/organization/OrganizationSettings';
import TeamManagement from '@/pages/organization/TeamManagement';


// Dashboard Router - redirects to correct dashboard based on role
const DashboardRouter = () => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  switch (user.role) {
    case 'candidate':
      return <CandidateDashboard />;
    case 'recruiter':
      return <RecruiterDashboard />;
    case 'hiring_manager':
      return <HiringManagerDashboard />;
    case 'hr_admin':
      return <HRAdminDashboard />;
    case 'org_owner':
      return <OrgOwnerDashboard />;
    case 'super_admin':
      return <SuperAdminDashboard />;
    default:
      return <Navigate to="/login" replace />;
  }
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/careers" element={<CareersPage />} />

      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/get-started" element={<AccountTypeSelection />} />
      <Route path="/signup" element={<IndividualSignup />} />
      <Route path="/signup/organization" element={<OrganizationSignup />} />
      <Route path="/join-team" element={<JoinTeamPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Error Pages */}
      <Route path="/access-denied" element={<AccessDenied />} />

      {/* Protected Profile Routes */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <ProtectedRoute>
            <ProfileEditPage />
          </ProtectedRoute>
        }
      />

      {/* Protected Organization Routes */}
      <Route
        path="/organization/settings"
        element={
          <ProtectedRoute allowedRoles={['hr_admin', 'org_owner']}>
            <OrganizationSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organization/team"
        element={
          <ProtectedRoute allowedRoles={['hr_admin', 'org_owner']}>
            <TeamManagement />
          </ProtectedRoute>
        }
      />

      {/* Protected Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <TenantProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </TenantProvider>
  );
}

export default App;
